import { PRIVACY_RIGHTS, formatDateTime, type PrivacyRight } from '@erp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, EyeOff, FileArchive } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { reauthenticate } from '../../lib/admin-api.ts';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import { refreshRequests, useContactRequests } from '../../lib/privacy-requests-api.ts';
import { useCan, useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { StepUpCode } from '../admin/step-up.tsx';
import { Alert } from '../ui/alert.tsx';
import { Button, buttonVariants } from '../ui/button.tsx';
import { Dialog } from '../ui/dialog.tsx';
import { Field, Input, Select } from '../ui/field.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

interface Contact {
  id: string;
  name: string;
}

/** A note of a shared deal left for review after anonymizing (mirrors the API). */
export interface ReviewItem {
  kind: 'deal_note' | 'activity';
  id: string;
  deal: { id: string; title: string };
  text: string;
  at: string;
}

const reviewKey = (contactId: string) => ['contacts', contactId, 'anonymization-review'] as const;

export const useAnonymizationReview = (contactId: string, enabled: boolean) =>
  useQuery({
    queryKey: reviewKey(contactId),
    queryFn: () => api<ReviewItem[]>(`/contacts/${contactId}/anonymization-review`),
    enabled,
  });

interface ExportLink {
  url: string;
  expiresAt: string;
  records: number;
}

/** The contact's open requests for some rights (to link the export or anonymization). */
function useOpenRequests(contactId: string, rights: PrivacyRight[]) {
  const requests = useContactRequests(contactId);
  return (requests.data ?? []).filter(
    (r) => rights.includes(r.right) && (r.status === 'received' || r.status === 'in_progress'),
  );
}

function RequestSelect({
  options,
  value,
  onChange,
}: {
  options: { id: string; folio: number; right: PrivacyRight }[];
  value: string;
  onChange: (value: string) => void;
}) {
  if (options.length === 0) return null;
  return (
    <Field label="Solicitud que atiende" hint="Queda registrado en el historial de la solicitud.">
      {(a11y) => (
        <Select
          {...a11y}
          value={value}
          onChange={(e) => {
            onChange(e.target.value);
          }}
        >
          <option value="">Ninguna</option>
          {options.map((r) => (
            <option key={r.id} value={r.id}>
              N.º {r.folio} · {PRIVACY_RIGHTS[r.right].label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * Access and portability (HU-CON-16): a .zip with everything the agency keeps about
 * the person, after a 2FA code; the link expires in 24 hours.
 */
export function ExportDialog({
  contact,
  requestId,
  onClose,
}: {
  contact: Contact;
  requestId?: string;
  onClose: () => void;
}) {
  const { data: session } = useSession();
  const client = useQueryClient();
  const requests = useOpenRequests(contact.id, ['access', 'portability']);
  const [linked, setLinked] = useState(requestId ?? '');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState<ExportLink | null>(null);

  const generate = async () => {
    setSaving(true);
    setError(null);
    try {
      await reauthenticate(code);
      const created = await api<ExportLink>(`/contacts/${contact.id}/export`, {
        method: 'POST',
        body: linked ? { requestId: linked } : {},
      });
      setLink(created);
      await refreshRequests(client);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Exportar sus datos"
      description="Un .zip con todo lo que la inmobiliaria guarda de esta persona: una hoja de cálculo para leerlo y un archivo estructurado (JSON) para llevarlo a otro servicio. La descarga queda en la bitácora."
    >
      {link ? (
        <div className="space-y-4">
          <Alert tone="success" title="Exportación lista">
            {link.records} registros. El enlace vence el{' '}
            {formatDateTime(link.expiresAt, session?.tenant.timezone)}.
          </Alert>
          <a href={link.url} download className={buttonVariants({ variant: 'brand' })}>
            <Download /> Descargar el .zip
          </a>
          <p className="text-sm text-muted-foreground">
            Envíaselo a la persona por un medio seguro (por ejemplo, un correo que ella confirmó) y
            registra la respuesta en su solicitud.
          </p>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void generate();
          }}
        >
          <RequestSelect options={requests} value={linked} onChange={setLinked} />
          <StepUpCode onChange={setCode} />
          {error && <Alert tone="danger">{error}</Alert>}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="brand" loading={saving} disabled={code.length !== 6}>
              <FileArchive /> Generar exportación
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}

/**
 * Cancellation and erasure (HU-CON-16): irreversible, confirmed by typing the name.
 * "Conservar por obligación legal" keeps name, identity document and address with the
 * processing limited; "olvido" also revokes the public links of their properties.
 */
export function AnonymizeDialog({
  contact,
  requestId,
  onClose,
}: {
  contact: Contact;
  requestId?: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const notify = useToast();
  const requests = useOpenRequests(contact.id, ['cancellation', 'erasure']);
  const [linked, setLinked] = useState(requestId ?? '');
  const linkedRight = requests.find((r) => r.id === linked)?.right;
  const [mode, setMode] = useState<'full' | 'retain'>('full');
  const [reason, setReason] = useState('');
  const [forget, setForget] = useState<boolean | null>(null);
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [review, setReview] = useState<ReviewItem[] | null>(null);
  // Erasure ("olvido") revokes public links by default.
  const forgetting = forget ?? linkedRight === 'erasure';
  const matches =
    confirm.trim().replace(/\s+/g, ' ').toLowerCase() ===
    contact.name.trim().replace(/\s+/g, ' ').toLowerCase();

  const anonymize = async () => {
    setSaving(true);
    setError(null);
    try {
      const done = await api<{ review: ReviewItem[] }>(`/contacts/${contact.id}/anonymize`, {
        method: 'POST',
        body: {
          confirmName: confirm,
          mode,
          ...(mode === 'retain' ? { retentionReason: reason } : {}),
          forget: forgetting,
          ...(linked ? { requestId: linked } : {}),
        },
      });
      await refreshRequests(client);
      await client.invalidateQueries({ queryKey: ['deals'] });
      await client.invalidateQueries({ queryKey: ['activities'] });
      notify('Contacto anonimizado.', 'success');
      client.setQueryData(reviewKey(contact.id), done.review);
      if (done.review.length > 0) setReview(done.review);
      else onClose();
    } catch (e) {
      setError(
        e instanceof ApiError && e.issues.length > 0
          ? (e.issues[0]?.message ?? errorMessage(e))
          : errorMessage(e),
      );
    } finally {
      setSaving(false);
    }
  };

  if (review) {
    return (
      <Dialog
        open
        onClose={onClose}
        title="Revisa estas notas"
        description="Participaba en negocios de otros clientes. Se reemplazó lo que lo nombraba completo o traía su teléfono o correo; revisa si estas otras lo mencionan."
      >
        <ReviewList contactId={contact.id} />
        <div className="mt-4 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            Terminar después
          </Button>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      open
      onClose={onClose}
      title="Anonimizar el contacto"
      description="No se puede deshacer."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void anonymize();
        }}
      >
        <Alert tone="warning" title="Qué pasará">
          <ul className="list-disc space-y-0.5 pl-4">
            <li>Se borran sus teléfonos, correos, notas, etiquetas y relaciones.</li>
            <li>Sus negocios abiertos se marcan como perdidos («Solicitud del titular»).</li>
            <li>
              Los títulos de sus negocios y actividades dejan de nombrarlo, y las notas se
              reemplazan por «[Eliminado por solicitud del titular]».
            </li>
            <li>
              Se conservan los montos, fechas y consentimientos, sin datos que lo identifiquen.
            </li>
          </ul>
        </Alert>
        <RequestSelect options={requests} value={linked} onChange={setLinked} />
        <fieldset className="space-y-1">
          <legend className="mb-1 text-sm font-medium">Qué se hace con sus datos</legend>
          <label className="flex min-h-11 items-start gap-3 text-sm">
            <input
              type="radio"
              name="mode"
              className="mt-0.5 size-5"
              checked={mode === 'full'}
              onChange={() => {
                setMode('full');
              }}
            />
            <span>
              <span className="font-medium">Anonimizar todo</span>
              <span className="block text-muted-foreground">
                También el nombre, el DUI y la dirección.
              </span>
            </span>
          </label>
          <label className="flex min-h-11 items-start gap-3 text-sm">
            <input
              type="radio"
              name="mode"
              className="mt-0.5 size-5"
              checked={mode === 'retain'}
              onChange={() => {
                setMode('retain');
              }}
            />
            <span>
              <span className="font-medium">Conservar lo que la ley obliga</span>
              <span className="block text-muted-foreground">
                Nombre, DUI y dirección se quedan con el tratamiento limitado (solo se guardan).
              </span>
            </span>
          </label>
        </fieldset>
        {mode === 'retain' && (
          <Field label="Qué obligación legal exige conservarlos">
            {(a11y) => (
              <textarea
                {...a11y}
                rows={2}
                maxLength={500}
                className={textarea}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            )}
          </Field>
        )}
        <label className="flex min-h-11 items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 size-5"
            checked={forgetting}
            onChange={(e) => {
              setForget(e.target.checked);
            }}
          />
          <span>
            <span className="font-medium">Revocar sus enlaces públicos (olvido)</span>
            <span className="block text-muted-foreground">
              Los enlaces compartidos de las propiedades donde es propietario dejan de funcionar.
            </span>
          </span>
        </label>
        <Field label={`Para confirmar, escribe su nombre: ${contact.name}`}>
          {(a11y) => (
            <Input
              {...a11y}
              autoComplete="off"
              value={confirm}
              onChange={(e) => {
                setConfirm(e.target.value);
              }}
            />
          )}
        </Field>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="danger"
            loading={saving}
            disabled={!matches || (mode === 'retain' && reason.trim().length < 5)}
          >
            <EyeOff /> Anonimizar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** On the contact's privacy card, for whoever handles privacy. */
export function ContactRightsActions({ contact }: { contact: Contact & { anonymized: boolean } }) {
  const canManage = useCan('privacy.manage');
  const [open, setOpen] = useState<'export' | 'anonymize' | null>(null);
  if (!canManage) return null;
  const close = () => {
    setOpen(null);
  };
  return (
    <div className="space-y-2 border-t border-border pt-4">
      <h3 className="text-sm font-medium">Derechos del titular</h3>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setOpen('export');
          }}
        >
          <FileArchive /> Exportar sus datos
        </Button>
        {!contact.anonymized && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setOpen('anonymize');
            }}
          >
            <EyeOff /> Anonimizar
          </Button>
        )}
      </div>
      {open === 'export' && <ExportDialog contact={contact} onClose={close} />}
      {open === 'anonymize' && <AnonymizeDialog contact={contact} onClose={close} />}
    </div>
  );
}

/** Each note: replace it, or leave it as is (it does not mention the person). */
export function ReviewList({ contactId }: { contactId: string }) {
  const client = useQueryClient();
  const notify = useToast();
  const { data: session } = useSession();
  const items = useAnonymizationReview(contactId, true);
  const [busy, setBusy] = useState<string | null>(null);
  const settle = async (item: ReviewItem, action: 'redact' | 'keep') => {
    setBusy(item.id);
    try {
      const left = await api<ReviewItem[]>(`/contacts/${contactId}/anonymization-review`, {
        method: 'POST',
        body: { kind: item.kind, itemId: item.id, action },
      });
      client.setQueryData(reviewKey(contactId), left);
      await client.invalidateQueries({ queryKey: ['deals'] });
      notify(action === 'redact' ? 'Nota reemplazada.' : 'Nota revisada.', 'success');
    } catch (e) {
      notify(errorMessage(e), 'error');
    } finally {
      setBusy(null);
    }
  };
  if (!items.data) return null;
  if (items.data.length === 0) {
    return <p className="text-sm text-muted-foreground">No quedan notas por revisar.</p>;
  }
  return (
    <ul className="divide-y divide-border">
      {items.data.map((item) => (
        <li key={item.id} className="space-y-2 py-3">
          <p className="text-xs text-muted-foreground">
            <Link to={`/negocios/${item.deal.id}`} className="text-primary underline">
              {item.deal.title}
            </Link>{' '}
            · {item.kind === 'activity' ? 'Actividad' : 'Nota'} del{' '}
            {formatDateTime(item.at, session?.tenant.timezone)}
          </p>
          <p className="text-sm whitespace-pre-line">{item.text}</p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              loading={busy === item.id}
              onClick={() => void settle(item, 'redact')}
            >
              Reemplazar
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={busy === item.id}
              onClick={() => void settle(item, 'keep')}
            >
              No lo menciona
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** On an anonymized contact: notes of shared deals still to review (privacy.manage). */
export function PendingReview({ contactId }: { contactId: string }) {
  const canManage = useCan('privacy.manage');
  const items = useAnonymizationReview(contactId, canManage);
  const [open, setOpen] = useState(false);
  const count = items.data?.length ?? 0;
  if (!canManage || count === 0) return null;
  return (
    <div className="mt-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={() => {
          setOpen(true);
        }}
      >
        {count === 1 ? 'Revisar 1 nota' : `Revisar ${String(count)} notas`} de negocios compartidos
      </Button>
      {open && (
        <Dialog
          open
          onClose={() => {
            setOpen(false);
          }}
          title="Revisa estas notas"
          description="Participaba en negocios de otros clientes; revisa si estas notas lo mencionan."
        >
          <ReviewList contactId={contactId} />
        </Dialog>
      )}
    </div>
  );
}
